import { useEffect, useRef, useCallback, useState } from 'react';
import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  LogLevel,
  HttpTransportType,
} from '@microsoft/signalr';
import { useAuthStore, decodeJwtPayload, extractRoles } from '@/stores/authStore';
import { permissionsForRoles } from '@/auth/permissions';
import {
  useConversationStore,
  markPendingClaim,
  clearPendingClaim,
  type OwnershipContext,
} from '@/stores/conversationStore';
import { useAgentStore } from '@/stores/agentStore';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type {
  ConsultationNotification,
  BotConsultationFollowupNotification,
  ProactiveGuidanceDeliveryFailedNotification,
  MessageNotification,
  SlaWarningNotification,
  SessionCreatedNotification,
  SessionStateChangedNotification,
  SessionClosedNotification,
  SessionCategoryChangedNotification,
  SessionHiddenNotification,
  SessionRestoredNotification,
  CustomerDisplayNameUpdatedNotification,
  OutboundMessageNotification,
  OutboundDeliveryFailedNotification,
  EmailDraftNotification,
  EmailDraftResolvedNotification,
  TimelineEntryNotification,
  SessionOwnershipChangedNotification,
  SessionWithdrawnNotification,
  SessionSharedNotification,
  SessionTagsChangedNotification,
  TagCatalogChangedNotification,
  ConsoleConfigChangedNotification,
  SessionSubjectChangedNotification,
  ConsultationProposedRepliesNotification,
  ReturnToTeamResult,
  AgentAvailability,
} from '@/types';

export type HubConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface UseAgentHubOptions {
  /**
   * OP-1005 - called AFTER the tombstone for a restored conversation has been lifted
   * (`ReceiveSessionRestored`), so the owner of the session list can reload it and the row comes
   * back. The hub cannot reload the list itself: the list loader lives in ConsoleShell, which is
   * the thing that calls this hook. Held in a ref, so a new callback identity never tears the
   * connection down.
   */
  onSessionRestored?: (sessionId: string) => void;
  /**
   * ADR-033 - the console must RE-READ this row from the server (GET /api/sessions/{id}). Called after every
   * ownership-relevant push (ownership changed, shared, a withdraw that is ambiguous), after a claim (guidance /
   * takeover / draft approve|edit) whose ownership push did not settle the row to "mine", and after a ReturnToTeam
   * (whatever its outcome). The hub never decides ownership; it only says "this row's owner may have changed".
   * Content-free pushes (`Withdrawn`, `Shared`) carry no owner, so the re-read is the only thing that settles them.
   */
  onRowStale?: (sessionId: string) => void;
  /**
   * The per-folder counts may have moved (a row was created/closed/claimed/shared/re-tagged, a message arrived, a
   * draft appeared or resolved). Fires often - the owner (ConsoleShell) is expected to coalesce it into one counts GET.
   */
  onCountsStale?: () => void;
  /** `ReceiveTagCatalogChanged` - the tenant's tag catalog changed (Admin Portal only edits it); refetch it. */
  onCatalogStale?: () => void;
  /**
   * `ReceiveConsoleConfigChanged` - the tenant's console settings or conversation statuses changed (Admin Portal only
   * edits them); refetch both. Content-free: nothing is applied from the push itself.
   */
  onConsoleConfigStale?: () => void;
  /**
   * The hub connected for the FIRST time in this effect's lifetime (the initial `start()`). Everything the console
   * loaded before this point was loaded without a live push channel behind it.
   */
  onConnected?: () => void;
  /**
   * The hub came back after being down (SignalR's automatic reconnect, or this hook's own recovery loop). Every push
   * sent while it was down is LOST (SignalR does not replay), so the owner must reload what the pushes would have
   * kept current: the folder pages, the counts, the open row, the drafts and consultations.
   */
  onReconnected?: () => void;
}

/**
 * Who this console is, read FRESH at the moment of each event (never captured at mount): `me` is the signed-in staff
 * id and `viewAll` is `workspace.viewAll` decoded from the token's role claims. Nothing here comes from a push.
 * FAIL CLOSED: a missing user, a missing token or an undecodable token yields `me=null` / `viewAll=false`, which is
 * the narrowest reading (nothing is mine, no read-only access to others' conversations).
 */
export function readOwnershipContext(): OwnershipContext {
  const { user, token } = useAuthStore.getState();
  const me = user?.agentId || null;
  let viewAll = false;
  try {
    viewAll = !!token && permissionsForRoles(extractRoles(decodeJwtPayload(token))).has('workspace.viewAll');
  } catch {
    viewAll = false;
  }
  return { me, viewAll };
}

/**
 * SignalR hook for connecting to /agenthub.
 * Auto-connects when authenticated, auto-reconnects on disconnect.
 * Wires server-to-client events to Zustand stores.
 */
export function useAgentHub(options?: UseAgentHubOptions) {
  const connectionRef = useRef<HubConnection | null>(null);
  const [status, setStatus] = useState<HubConnectionStatus>('disconnected');
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const onSessionRestoredRef = useRef(options?.onSessionRestored);
  onSessionRestoredRef.current = options?.onSessionRestored;

  // Every callback is held in a ref: a new callback identity must never tear the connection down.
  const onRowStaleRef = useRef(options?.onRowStale);
  onRowStaleRef.current = options?.onRowStale;
  const onCountsStaleRef = useRef(options?.onCountsStale);
  onCountsStaleRef.current = options?.onCountsStale;
  const onCatalogStaleRef = useRef(options?.onCatalogStale);
  onCatalogStaleRef.current = options?.onCatalogStale;
  const onConsoleConfigStaleRef = useRef(options?.onConsoleConfigStale);
  onConsoleConfigStaleRef.current = options?.onConsoleConfigStale;
  const onConnectedRef = useRef(options?.onConnected);
  onConnectedRef.current = options?.onConnected;
  const onReconnectedRef = useRef(options?.onReconnected);
  onReconnectedRef.current = options?.onReconnected;

  const handleConsultation = useConversationStore((s) => s.handleConsultation);
  const handleBotConsultationFollowup = useConversationStore((s) => s.handleBotConsultationFollowup);
  const handleProactiveGuidanceDeliveryFailed = useConversationStore(
    (s) => s.handleProactiveGuidanceDeliveryFailed,
  );
  const handleMessageNotification = useConversationStore((s) => s.handleMessageNotification);
  const handleSlaWarning = useConversationStore((s) => s.handleSlaWarning);
  const handleSessionCreated = useConversationStore((s) => s.handleSessionCreated);
  const handleSessionStateChanged = useConversationStore((s) => s.handleSessionStateChanged);
  const handleSessionClosed = useConversationStore((s) => s.handleSessionClosed);
  const handleSessionCategoryChanged = useConversationStore((s) => s.handleSessionCategoryChanged);
  const handleSessionHidden = useConversationStore((s) => s.handleSessionHidden);
  const handleSessionRestored = useConversationStore((s) => s.handleSessionRestored);
  const handleCustomerDisplayNameUpdated = useConversationStore(
    (s) => s.handleCustomerDisplayNameUpdated,
  );
  const handleOutboundMessage = useConversationStore((s) => s.handleOutboundMessage);
  const handleOutboundDeliveryFailed = useConversationStore((s) => s.handleOutboundDeliveryFailed);
  const handleEmailDraft = useConversationStore((s) => s.handleEmailDraft);
  const handleEmailDraftResolved = useConversationStore((s) => s.handleEmailDraftResolved);
  const handleTimelineEntry = useConversationStore((s) => s.handleTimelineEntry);
  const handleSessionOwnershipChanged = useConversationStore((s) => s.handleSessionOwnershipChanged);
  const handleSessionWithdrawn = useConversationStore((s) => s.handleSessionWithdrawn);
  const handleSessionShared = useConversationStore((s) => s.handleSessionShared);
  const handleSessionTagsChanged = useConversationStore((s) => s.handleSessionTagsChanged);
  const handleTagCatalogChanged = useConversationStore((s) => s.handleTagCatalogChanged);
  const handleConsoleConfigChanged = useConversationStore((s) => s.handleConsoleConfigChanged);
  const handleSessionSubjectChanged = useConversationStore((s) => s.handleSessionSubjectChanged);
  const handleConsultationProposedReplies = useConversationStore((s) => s.handleConsultationProposedReplies);
  const addAgent = useAgentStore((s) => s.addAgent);
  const removeAgent = useAgentStore((s) => s.removeAgent);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return;
    }

    // Fallback when VITE_SIGNALR_HUB_URL is missing — a missing url used to throw
    // "The 'url' argument is required" at connection-builder time and took down the
    // whole app via the ErrorBoundary. Prod resolves the root-relative '/agenthub'
    // (Caddy proxies the hub on the same origin); dev keeps the localhost port. (T6.1)
    const hubUrl =
      import.meta.env.VITE_SIGNALR_HUB_URL ||
      (import.meta.env.DEV ? 'http://localhost:5011/agenthub' : '/agenthub');

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => useAuthStore.getState().token ?? '',
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 30_000])
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    // Server-to-client event handlers
    connection.on('ReceiveConsultation', (notification: ConsultationNotification) => {
      handleConsultation(notification);
    });

    // HC1.D (2026-05-13) — bot's clarifying-question-to-teammate channel.
    // Fires when the AiAgent's guidance examiner judged the agent's
    // earlier guidance too vague to act on. Renders as another bot-side
    // message in the existing Bot Consultation pane (same session card).
    connection.on(
      'ReceiveBotConsultationFollowup',
      (notification: BotConsultationFollowupNotification) => {
        handleBotConsultationFollowup(notification);
      },
    );

    // HC2.2 (2026-05-27) — actionable proactive guidance could not be
    // delivered to the customer (compose failed / content filter rejected)
    // and there was NO open consultation to attach a followup to. Surface a
    // live alert in the Bot Consultation pane + bump the row's urgency so
    // the operator who just typed the guidance knows it didn't land.
    connection.on(
      'ReceiveProactiveGuidanceDeliveryFailed',
      (notification: ProactiveGuidanceDeliveryFailedNotification) => {
        handleProactiveGuidanceDeliveryFailed(notification);
      },
    );

    connection.on('ReceiveMessage', (notification: MessageNotification) => {
      handleMessageNotification(notification);
      onCountsStaleRef.current?.();
    });

    connection.on('ReceiveSlaWarning', (notification: SlaWarningNotification) => {
      handleSlaWarning(notification);
    });

    // Session lifecycle — drives live list updates (no refresh needed).
    connection.on('ReceiveSessionCreated', (notification: SessionCreatedNotification) => {
      handleSessionCreated(notification);
      onCountsStaleRef.current?.();
    });

    connection.on('ReceiveSessionStateChanged', (notification: SessionStateChangedNotification) => {
      handleSessionStateChanged(notification);
      onCountsStaleRef.current?.();
    });

    connection.on('ReceiveSessionClosed', (notification: SessionClosedNotification) => {
      handleSessionClosed(notification);
      onCountsStaleRef.current?.();
    });

    // ADR-017 D2 — a session's per-tenant Category changed; move the row to its
    // new console directory section live (parallel to ReceiveSessionStateChanged,
    // but a pure classification change, never a lifecycle transition).
    connection.on(
      'ReceiveSessionCategoryChanged',
      (notification: SessionCategoryChangedNotification) => {
        handleSessionCategoryChanged(notification);
        onCountsStaleRef.current?.();
      },
    );

    // OP-1005 - a staff member (another agent, or this very console) marked an EMAIL conversation as
    // spam / unwanted: drop the row at once and tombstone it so no reload / late push brings it back.
    connection.on('ReceiveSessionHidden', (notification: SessionHiddenNotification) => {
      handleSessionHidden(notification);
      onCountsStaleRef.current?.();
    });

    // OP-1005 - someone pressed Undo inside the grace window. Lift the tombstone FIRST (the list
    // reload filters on it), then ask the shell to reload the session list so the row returns.
    connection.on('ReceiveSessionRestored', (notification: SessionRestoredNotification) => {
      handleSessionRestored(notification);
      onSessionRestoredRef.current?.(notification?.sessionId);
      onCountsStaleRef.current?.();
    });

    // 2026-07-23 — a customer's canonical display name resolved/changed AFTER one or more
    // of their sessions were already created; patch every matching open row live so a
    // console that stays connected all shift doesn't need a reload to self-heal.
    connection.on(
      'ReceiveCustomerDisplayNameUpdated',
      (notification: CustomerDisplayNameUpdatedNotification) => {
        handleCustomerDisplayNameUpdated(notification);
      },
    );

    // Outbound messages (AI or agent) — fill in the other half of the thread
    // live. Without this the agent only sees customer messages in real time.
    connection.on('ReceiveOutboundMessage', (notification: OutboundMessageNotification) => {
      handleOutboundMessage(notification);
    });

    // OP-681 - the counterweight to the push above, which reports every outbound message as sent. An
    // agent who took over an email or WhatsApp conversation used to see their reply land in the thread
    // with a delivered tick while nothing had been transmitted; this is the only signal that says so.
    connection.on(
      'ReceiveOutboundDeliveryFailed',
      (notification: OutboundDeliveryFailedNotification) => {
        handleOutboundDeliveryFailed(notification);
      },
    );

    // ADR-019 D5 — bot drafted (or re-drafted) an Email reply gated for human
    // review; route it onto the row's pendingDraft so the review card renders.
    connection.on('ReceiveEmailDraft', (notification: EmailDraftNotification) => {
      handleEmailDraft(notification);
      onCountsStaleRef.current?.();
    });

    // ADR-019 D5 — a review draft reached a terminal outcome (or was claimed by
    // another agent, outcome 'rejectedNotOwner'); route it so the card clears
    // or shows the ownership notice.
    connection.on('ReceiveEmailDraftResolved', (notification: EmailDraftResolvedNotification) => {
      handleEmailDraftResolved(notification);
      onCountsStaleRef.current?.();
    });

    // Chat action timeline (staff-only) - ConversationStoreService stored a new line (a takeover, a hand-back,
    // who the bot consulted, a close ...) and the hub fans it to this tenant's STAFF group. The store keeps it
    // only for the open conversation and de-duplicates on entryId. Best effort: a reload is authoritative.
    connection.on('ReceiveTimelineEntry', (notification: TimelineEntryNotification) => {
      handleTimelineEntry(notification);
    });

    // ─── ADR-033 private ownership (CONTRACT-C) ─────────────────────────────────────────────────────────────
    // The server pushes these only when private ownership is in effect (PR-A2); until then none arrives and the
    // console lives on the reloads it already does. None of the pushes carries a conversation, so every one that
    // can change WHO may see a row ends in a row re-read (onRowStale) rather than a guess. `me` / `viewAll` are read
    // fresh per event (readOwnershipContext), never captured at mount.

    // Someone claimed, was handed, or released the conversation. The store records the new owner (and hides the row
    // when it is somebody else's and this console is not viewAll); the re-read is what makes it authoritative.
    connection.on(
      'ReceiveSessionOwnershipChanged',
      (notification: SessionOwnershipChangedNotification) => {
        handleSessionOwnershipChanged(notification, readOwnershipContext());
        if (notification?.sessionId) onRowStaleRef.current?.(notification.sessionId);
        onCountsStaleRef.current?.();
      },
    );

    // Somebody else claimed a conversation this console may be showing. 'hide' = the store already dropped the row;
    // 'verify' = the row is mine / a claim of mine is in flight / it is not known to be under private ownership, so ask
    // the server who owns it now; 'ignore' = viewAll.
    connection.on('ReceiveSessionWithdrawn', (notification: SessionWithdrawnNotification) => {
      const outcome = handleSessionWithdrawn(notification, readOwnershipContext());
      if (outcome === 'verify' && notification?.sessionId) onRowStaleRef.current?.(notification.sessionId);
      onCountsStaleRef.current?.();
    });

    // The owner was cleared: team-shared again. Content-free, so re-read the row to learn its state.
    connection.on('ReceiveSessionShared', (notification: SessionSharedNotification) => {
      handleSessionShared(notification);
      if (notification?.sessionId) onRowStaleRef.current?.(notification.sessionId);
      onCountsStaleRef.current?.();
    });

    // Tags moved (the store applies them only when tagsVersion is newer). Tag folders' counts move with them.
    connection.on('ReceiveSessionTagsChanged', (notification: SessionTagsChangedNotification) => {
      handleSessionTagsChanged(notification);
      onCountsStaleRef.current?.();
    });

    // The tenant's tag catalog changed (Admin Portal). Bump the store's catalogVersion and ask for a refetch; the
    // tag folders' counts follow the catalog.
    connection.on('ReceiveTagCatalogChanged', (notification: TagCatalogChangedNotification) => {
      void notification;
      handleTagCatalogChanged();
      onCatalogStaleRef.current?.();
      onCountsStaleRef.current?.();
    });

    // PR-A - the tenant's console settings (private conversations, hide tag folders) or its conversation statuses changed
    // (Admin Portal). Same shape as the catalog push: bump the store's consoleConfigVersion and ask for a refetch of BOTH;
    // the status folders' counts and (with private conversations flipped) every folder's visibility follow it.
    connection.on('ReceiveConsoleConfigChanged', (notification: ConsoleConfigChangedNotification) => {
      void notification;
      handleConsoleConfigChanged();
      onConsoleConfigStaleRef.current?.();
      onCountsStaleRef.current?.();
    });

    // A conversation was renamed (or its subject cleared). This push existed server-side and was never registered here.
    connection.on('ReceiveSessionSubjectChanged', (notification: SessionSubjectChangedNotification) => {
      handleSessionSubjectChanged(notification);
    });

    // 2026-10-28 - the replies the bot proposes under one of its consultation notes (staff-only). Can land a few seconds
    // after the note, or before it is on screen; the store attaches them to that note or holds them until it arrives.
    connection.on(
      'ReceiveConsultationProposedReplies',
      (notification: ConsultationProposedRepliesNotification) => {
        handleConsultationProposedReplies(notification);
      },
    );

    connection.on('AgentJoined', (agentId: string) => {
      addAgent(agentId);
    });

    connection.on('AgentLeft', (agentId: string) => {
      removeAgent(agentId);
    });

    /**
     * Re-read the agent's own availability from the hub after every successful
     * (re)connect.
     *
     * The store seeds `myAvailability` to 'OFFLINE' and, before 2026-08-02,
     * nothing ever corrected it — the value only moved when the agent picked
     * one from the menu. So a refresh always redrew the default and the agent's
     * Busy/Away selection appeared to have been discarded. (The server-side half
     * of that bug was `ConnectAsync` overwriting the stored choice with ONLINE;
     * both halves had to go.)
     *
     * Deliberately AFTER connect: `ConnectAsync` restores the agent's preferred
     * value on the server, so reading before it would race the restore.
     * Best-effort — a failed read just leaves the current value alone.
     */
    const hydrateAvailability = async () => {
      const agentId = useAuthStore.getState().user?.agentId;
      if (!agentId) return;
      try {
        const resp = await axiosClient.get<Array<{ agentId: string; availability: AgentAvailability }>>(
          API.AGENTS.SESSIONS(agentId),
        );
        const mine = (resp.data ?? []).find((s) => s.agentId === agentId) ?? resp.data?.[0];
        if (mine?.availability) {
          useAgentStore.getState().setMyAvailability(mine.availability);
        }
      } catch {
        /* keep whatever the store holds */
      }
    };

    connection.onreconnecting(() => setStatus('reconnecting'));
    connection.onreconnected(() => {
      setStatus('connected');
      void hydrateAvailability();
      // Pushes sent while the hub was down are gone; the owner reloads what they would have kept current.
      onReconnectedRef.current?.();
    });

    // withAutomaticReconnect's own ladder ([0,2s,5s,10s,30s]) gives up after
    // ~47s and fires onclose permanently — without a recovery loop the staff
    // console goes deaf until the agent manually refreshes (OP-327). Keep
    // retrying with a 5s->30s capped backoff for as long as this effect stays
    // mounted (i.e. the agent stays authenticated); 'reconnecting' covers both
    // the wait and the attempt so the banner stays up the whole time.
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelayMs = 5_000;

    const scheduleReconnect = () => {
      if (stopped) return;
      reconnectTimer = setTimeout(() => {
        if (stopped) return;
        connection
          .start()
          .then(() => {
            reconnectDelayMs = 5_000;
            setStatus('connected');
            void hydrateAvailability();
            // Recovery after the automatic ladder gave up (or the first start failed): same as a reconnect.
            if (!stopped) onReconnectedRef.current?.();
          })
          .catch(() => {
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30_000);
            scheduleReconnect();
          });
      }, reconnectDelayMs);
    };

    connection.onclose(() => {
      setStatus('reconnecting');
      scheduleReconnect();
    });

    setStatus('connecting');
    connection
      .start()
      .then(() => {
        setStatus('connected');
        void hydrateAvailability();
        if (!stopped) onConnectedRef.current?.();
      })
      .catch(() => {
        setStatus('reconnecting');
        scheduleReconnect();
      });

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      connection.stop();
      connectionRef.current = null;
      setStatus('disconnected');
    };
  // Every handler read from a store MUST appear here — a registration whose
  // handler identity is missing from this array keeps firing the STALE
  // closure captured on first mount, which is silent and invisible in manual
  // testing. `useAgentHub.emailDraft.test.tsx` pins this.
  }, [
    isAuthenticated,
    token,
    handleConsultation,
    handleBotConsultationFollowup,
    handleProactiveGuidanceDeliveryFailed,
    handleMessageNotification,
    handleSlaWarning,
    handleSessionCreated,
    handleSessionStateChanged,
    handleSessionClosed,
    handleSessionCategoryChanged,
    handleSessionHidden,
    handleSessionRestored,
    handleCustomerDisplayNameUpdated,
    handleOutboundMessage,
    handleOutboundDeliveryFailed,
    handleEmailDraft,
    handleEmailDraftResolved,
    handleTimelineEntry,
    handleSessionOwnershipChanged,
    handleSessionWithdrawn,
    handleSessionShared,
    handleSessionTagsChanged,
    handleTagCatalogChanged,
    handleConsoleConfigChanged,
    handleSessionSubjectChanged,
    handleConsultationProposedReplies,
    addAgent,
    removeAgent,
  ]);

  // ─── Client-to-server invocations ──────────────────────────────

  /**
   * ADR-033 fallback for a LOST ownership push. After a claim (guidance / takeover / draft approve|edit) resolves, the
   * server has made this agent the owner; the `ReceiveSessionOwnershipChanged` push normally says so. When the row does
   * not already show this console's agent as the owner (the push has not landed, was lost, or the server sends none
   * yet) ask the shell to re-read the row. An unknown `me` re-reads too (fail safe: never assume it is mine).
   */
  const reReadRowIfNotMine = useCallback((sessionId: string) => {
    const me = useAuthStore.getState().user?.agentId || null;
    const row = useConversationStore.getState().conversations.find((c) => c.sessionId === sessionId);
    if (!me || row?.ownerAgentId !== me) onRowStaleRef.current?.(sessionId);
  }, []);

  /**
   * Send guidance to the bot. Available at any time — the bot relays the
   * guidance to the customer (with the configured prefix) regardless of
   * whether a consultation is open. The <c>channel</c> parameter is
   * forwarded so the relay targets the right customer channel even when
   * there's no pending-consultation entry for it to be read from.
   */
  const sendGuidance = useCallback(
    async (sessionId: string, message: string, channel: string, attachmentIds?: string[]): Promise<void> => {
      const conn = connectionRef.current;
      if (conn?.state !== HubConnectionState.Connected) {
        throw new Error('Agent hub not connected');
      }
      // ADR-033: guidance CLAIMS the conversation. Until the server's ownership push settles it, a withdraw that
      // lands for this session is ambiguous, so the store VERIFIES instead of hiding. A rejected send claims nothing.
      markPendingClaim(sessionId);
      try {
        // 2026-10-20 - pictures for the bot go to SendGuidanceWithAttachments (4 args). Text-only guidance keeps the
        // original 3-argument SendGuidance, so a console bundle and a hub of different ages still agree on every
        // guidance that carries no picture (SignalR binds by argument COUNT). Only ids travel: the hub re-resolves each.
        const ids = (attachmentIds ?? []).filter((id) => !!id && id.trim().length > 0);
        if (ids.length > 0) {
          await conn.invoke('SendGuidanceWithAttachments', sessionId, message, channel, ids);
        } else {
          await conn.invoke('SendGuidance', sessionId, message, channel);
        }
      } catch (err) {
        clearPendingClaim(sessionId);
        throw err;
      }
      reReadRowIfNotMine(sessionId);
    },
    [reReadRowIfNotMine]
  );

  const issueTakeover = useCallback(async (sessionId: string, reason: string): Promise<void> => {
    const conn = connectionRef.current;
    if (conn?.state !== HubConnectionState.Connected) {
      throw new Error('Agent hub not connected');
    }
    // ADR-033: a takeover CLAIMS the conversation (see sendGuidance).
    markPendingClaim(sessionId);
    try {
      await conn.invoke('IssueTakeover', sessionId, reason);
    } catch (err) {
      clearPendingClaim(sessionId);
      throw err;
    }
    reReadRowIfNotMine(sessionId);
  }, [reReadRowIfNotMine]);

  /**
   * Sends a message the agent typed directly to the customer in a session the agent has already
   * taken over, optionally carrying file attachments. Backing AgentHub method:
   * SendCustomerMessageWithAttachments. The server publishes AgentMessageSent;
   * RealTimeChatService broadcasts it to the customer's SignalR session group and
   * ConversationStoreService persists it, so the file survives a transcript reload.
   *
   * `attachmentIds` are media ids the console already uploaded. Only IDS travel: the hub
   * re-resolves every one against MediaAttachmentService, which re-checks the (tenant, session)
   * pairing and the scan verdict. Nothing this client claims about a file is trusted, and a
   * resolution failure REFUSES the whole send rather than delivering the text without its files.
   */
  const sendCustomerMessage = useCallback(
    async (sessionId: string, content: string, attachmentIds?: string[]): Promise<void> => {
      const conn = connectionRef.current;
      if (conn?.state !== HubConnectionState.Connected) {
        throw new Error('Agent hub not connected');
      }
      // Always the three-argument method, even with nothing attached. SignalR binds by argument
      // COUNT, so keeping one call shape sidesteps the "provides 2 but target expects 3" class
      // of failure entirely; the server keeps the old two-argument SendCustomerMessage alive
      // only so console bundles already open in a browser survive a deploy.
      await conn.invoke(
        'SendCustomerMessageWithAttachments',
        sessionId,
        content,
        attachmentIds && attachmentIds.length > 0 ? attachmentIds : null,
      );
    },
    []
  );

  const releaseSession = useCallback(async (sessionId: string): Promise<void> => {
    const conn = connectionRef.current;
    if (conn?.state !== HubConnectionState.Connected) {
      throw new Error('Agent hub not connected');
    }
    await conn.invoke('ReleaseSession', sessionId);
  }, []);

  /**
   * ADR-033 - hand the conversation back to the team (and the bot). Backing AgentHub method: `ReturnToTeam(sessionId)`,
   * exactly ONE argument (SignalR binds by argument count). Answers the server's `{ outcome, stateAfter }`.
   *
   * Two things this does NOT do, on purpose:
   *  - it never reports success for a refusal: every refusal is a HubException ("Session not available" for unknown /
   *    not yours, `forbidden` without `workspace.handle`) which SignalR delivers as a REJECTED invoke carrying that text,
   *    so the caller sees the same error shape as every other invoke and can use its refusal test. A throw means NOT
   *    returned;
   *  - it never lets the invoke's answer stand as the new state. A successful return can leave `stateAfter`
   *    HUMAN_CONTROLLED when another agent holds live control, so the row is ALWAYS re-read afterwards (success or
   *    failure) through `onRowStale`, and the counts with it. An answer that is not an object carrying a string
   *    `outcome` is treated as a failure (fail closed: an unreadable answer is not "returned").
   */
  const returnToTeam = useCallback(async (sessionId: string): Promise<ReturnToTeamResult> => {
    const conn = connectionRef.current;
    if (conn?.state !== HubConnectionState.Connected) {
      throw new Error('Agent hub not connected');
    }
    try {
      const result = await conn.invoke<ReturnToTeamResult>('ReturnToTeam', sessionId);
      if (!result || typeof result !== 'object' || typeof result.outcome !== 'string') {
        throw new Error('Unexpected ReturnToTeam answer');
      }
      return result;
    } finally {
      onRowStaleRef.current?.(sessionId);
      onCountsStaleRef.current?.();
    }
  }, []);

  const setAvailability = useCallback(async (availability: AgentAvailability): Promise<void> => {
    const conn = connectionRef.current;
    if (conn?.state !== HubConnectionState.Connected) {
      throw new Error('Agent hub not connected');
    }
    await conn.invoke('SetAvailability', availability);
    useAgentStore.getState().setMyAvailability(availability);
  }, []);

  /**
   * Ephemeral "I'm typing" signal — the agent console fires this every ~1s
   * while the takeover-agent presses keys in the reply box. Server publishes
   * <c>AgentTyping</c>; RealTimeChatService relays a SignalR notification to
   * the customer's chat widget which animates dots. No-op if the hub isn't
   * connected — typing is fire-and-forget UX, never worth surfacing an error.
   */
  const signalAgentTyping = useCallback(
    async (sessionId: string): Promise<void> => {
      const conn = connectionRef.current;
      if (conn?.state !== HubConnectionState.Connected) return;
      try {
        await conn.invoke('SignalAgentTyping', sessionId);
      } catch {
        // Ignore — typing is best-effort.
      }
    },
    []
  );

  /**
   * Bot voice Phase D (2026-10-31) - "I am typing guidance for the bot in this conversation". Backing AgentHub method:
   * `NotifyGuidanceComposing(sessionId)`, exactly ONE argument. It is NOT `SignalAgentTyping`: nothing reaches the
   * customer, it shows no dots and it never claims the conversation. The server writes a short-lived hint that lets the
   * customer's next bot turn wait until the teammate is done (only while the tenant's quiet gate is on). The caller
   * throttles it (Composer: at most once every 2000 ms, on text changes only). Best-effort like typing: a lost hint only
   * lets the bot start sooner, so a disconnected hub or a refusal is swallowed, never shown.
   */
  const notifyGuidanceComposing = useCallback(
    async (sessionId: string): Promise<void> => {
      const conn = connectionRef.current;
      if (!sessionId || conn?.state !== HubConnectionState.Connected) return;
      try {
        await conn.invoke('NotifyGuidanceComposing', sessionId);
      } catch {
        // Ignore - the hint is best-effort.
      }
    },
    []
  );

  /**
   * Dismisses an open consultation without providing guidance — the agent's
   * "Let bot continue" button. Backing AgentHub method: DismissConsultation.
   * Server publishes ConsultationDismissed { DismissedBy: "agent" } which
   * AiAgentService consumes (sends fallback reply to customer) and
   * SessionManagementService consumes (flips state back to ACTIVE so the
   * "WARNING" badge clears).
   */
  const dismissConsultation = useCallback(
    async (sessionId: string, botId: string, channel: string, reason?: string): Promise<void> => {
      const conn = connectionRef.current;
      if (conn?.state !== HubConnectionState.Connected) {
        throw new Error('Agent hub not connected');
      }
      await conn.invoke('DismissConsultation', sessionId, botId, channel, reason ?? null);
    },
    []
  );

  /**
   * ADR-019 D5/D6/D7 — relays an agent's decision on an email review draft to
   * AgentHub.ActOnEmailDraft. `view` is the "the human opened the card" touch that
   * resets AiAgent's Idle clock (ADR-019 D7); `approve`/`revise`/`edit` are the three
   * buttons. Ownership refusal (`rejectedNotOwner`) is decided server-side and comes
   * back as an EmailReplyDraftResolved push, never as an error here.
   */
  const actOnEmailDraft = useCallback(
    async (
      sessionId: string,
      draftId: string,
      action: 'view' | 'approve' | 'revise' | 'edit',
      text?: string,
      revision = 0,
    ): Promise<void> => {
      const conn = connectionRef.current;
      if (conn?.state !== HubConnectionState.Connected) {
        // `view` is a best-effort idle-clock touch (same policy as signalAgentTyping):
        // never surface an error banner for it. A substantive decision MUST throw so
        // ChatView's runAction shows the agent that their click did not land.
        if (action === 'view') return;
        throw new Error('Agent hub not connected');
      }
      if (action === 'view') {
        try {
          await conn.invoke('ActOnEmailDraft', sessionId, draftId, action, text ?? null, revision);
        } catch {
          // Best-effort — a lost view touch only costs an idle-clock reset; MaxWait still bounds the wait.
        }
        return;
      }
      // ADR-033: approving or editing a draft CLAIMS the conversation (revise only re-composes; it sends nothing).
      const claims = action === 'approve' || action === 'edit';
      if (claims) markPendingClaim(sessionId);
      try {
        await conn.invoke('ActOnEmailDraft', sessionId, draftId, action, text ?? null, revision);
      } catch (err) {
        if (claims) clearPendingClaim(sessionId);
        throw err;
      }
      if (claims) reReadRowIfNotMine(sessionId);
    },
    [reReadRowIfNotMine],
  );

  /**
   * 2026-10-28 - send one of the bot's proposed replies to the customer. Backing AgentHub method:
   * `SendProposedReply(sessionId, noteMessageId, index)`, exactly THREE arguments (SignalR binds by count). Only the
   * position travels: the SERVER sends the text it stored for that note, verbatim, so nothing the browser holds or edited
   * can reach the customer this way. A refusal is a rejected invoke and is thrown to the caller (nothing was sent).
   *
   * Treated like guidance for ownership: it acts on the conversation toward the customer, so a withdraw that lands while
   * it is in flight is VERIFIED rather than trusted, and the row is re-read afterwards unless it already says it is mine.
   * Both are harmless if the server does not count it as a claim.
   */
  const sendProposedReply = useCallback(
    async (sessionId: string, noteMessageId: string, index: number): Promise<void> => {
      const conn = connectionRef.current;
      if (conn?.state !== HubConnectionState.Connected) {
        throw new Error('Agent hub not connected');
      }
      markPendingClaim(sessionId);
      try {
        await conn.invoke('SendProposedReply', sessionId, noteMessageId, index);
      } catch (err) {
        clearPendingClaim(sessionId);
        throw err;
      }
      reReadRowIfNotMine(sessionId);
    },
    [reReadRowIfNotMine],
  );

  return {
    status,
    sendGuidance,
    sendProposedReply,
    issueTakeover,
    sendCustomerMessage,
    releaseSession,
    returnToTeam,
    setAvailability,
    dismissConsultation,
    signalAgentTyping,
    notifyGuidanceComposing,
    actOnEmailDraft,
  };
}
